import React, { useCallback, ReactElement, useEffect, useMemo } from "react";
import {
  FormikProps,
  useFormik,
  FormikProvider,
  useFormikContext,
  FormikHelpers,
} from "formik";

import { useRouter } from "next/router";

import * as yup from "yup";
import axios from "axios";
import debounce from "lodash/debounce";
import { triggerAsyncId } from "async_hooks";
import { ReffererBodyType } from "pages/api/referral-form-submit";
import { IFormGetUserResponse } from "@pages/api/lead-form-getuser";

declare global {
  interface Window {
    dataLayer: any[] | undefined;
    blueshift: any;
    blueshiftProductId: string | undefined;
  }
}

const validationSchema = yup.object().shape({
  email: yup
    .string()

    .email("Please enter a valid email address.")
    .required("Please enter a valid email address."),
  firstName: yup.string().min(1).required("Please enter your first name."),
  lastName: yup.string().min(1).required("Please enter your last name."),

  verification: yup.boolean().oneOf([true], "This field is required."),
});

export interface ReferralFormDataTypes extends ReffererBodyType {
  verification: boolean;
}

const initialValues: ReferralFormDataTypes = {
  email: "",
  firstName: "",
  lastName: "",
  referred_customer_id: "",
  referred_customer_first_name: "",
  referred_customer_last_name: "",
  verification: false,
  utm_campaign: "",
  utm_content: "",
  utm_medium: "",
  utm_source: "",
};

export type IReferralFormWrapperProps = {
  formik: FormikProps<ReferralFormDataTypes>;
  submitted: boolean;
};

const AutoSubmitToken = () => {
  // Grab values and submitForm from context
  const data = useFormikContext();
  const [sentFormStart, setSentFormStart] = React.useState<boolean>(false);

  React.useEffect(() => {
    // Submit the form imperatively as an effect as soon as form values.token are 6 digits long

    if (data.dirty && !sentFormStart) {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: "formStart",
        });
        setSentFormStart(true);

        // console.log("Sent form start");
      }
    }
  }, [data.dirty]);
  return <span />;
};

function ReferralForm({ children }: any): ReactElement {
  const formRef = React.useRef<HTMLFormElement>(null);
  const router = useRouter();
  const { customerId, utm_campaign, utm_content, utm_medium, utm_source } =
    router.query;
  const [submitted, setSubmitted] = React.useState<boolean>(false);

  const formChanged = React.useRef(false);

  const [referredCustomerId, setReferredCustomerId] = React.useState<string>();

  useEffect(() => {
    if (customerId) {
      setReferredCustomerId(customerId as string);
    }
  }, [customerId]);

  const handleSubmit = async (
    formData: ReferralFormDataTypes,
    { setStatus, validateForm }: FormikHelpers<ReferralFormDataTypes>
  ) => {
    const payload = {
      ...formData,
      xxTrustedFormCertUrl: formRef.current
        ? new FormData(formRef.current).get("xxTrustedFormCertUrl")
        : "",
    };

    console.log("Submitting form", payload);

    const validationResponse = await validateForm(formData);

    if (payload.referred_customer_id) {
      const response = await axios.get(
        `/api/lead-form-getuser?customer_id=${payload.referred_customer_id}`
      );

      const res = response?.data as IFormGetUserResponse;

      if (res && res?.user) {
        payload.referred_customer_first_name = res?.user?.firstname || "";
        payload.referred_customer_last_name = res?.user?.lastname || "";
      }
    }
    if (Object.keys(validationResponse).length > 0) {
      setStatus(validationResponse);
      return false;
    }

    return axios
      .post("/api/referral-form-submit", payload)
      .then(() => {
        setSubmitted(true);

        if (window.dataLayer) {
          window.dataLayer.push({
            event: "formSubmit",
          });
        }
      })
      .catch((error) => {
        //TODO: need to log this error to something
        console.error(error);
        setStatus(error.message);
        return false;
      })
      .finally(() => {
        router.push("#form-top");
      });
  };

  // pass through values to formik
  initialValues.referred_customer_id = referredCustomerId || "";
  initialValues.utm_campaign = utm_campaign?.toString() || "";
  initialValues.utm_content = utm_content?.toString() || "";
  initialValues.utm_medium = utm_medium?.toString() || "";
  initialValues.utm_source = utm_source?.toString() || "";

  const formik = useFormik<ReferralFormDataTypes>({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
    validateOnBlur: true,
    validateOnChange: false,
  });

  const t: IReferralFormWrapperProps = { formik, submitted };

  return (
    <FormikProvider value={formik}>
      <div id="form-top" />

      <AutoSubmitToken />
      <form
        noValidate
        role="form"
        aria-label="Contact information"
        onSubmit={formik?.handleSubmit}
        ref={formRef}
      >
        {children(t)}
      </form>
    </FormikProvider>
  );
}

export default React.memo(ReferralForm);
